/**
 * Control Room: Oversikt og Organisasjoner (sak 238, trinn 3; migrering 0479).
 *
 * `/v1/platform/overview`, `/v1/platform/organizations`, `/v1/platform/organizations/:id` og
 * `/v1/platform/organizations/:id/users`. Bare for plattformadminer; alle andre får 404.
 *
 * BARE TALL OG DE NAVNGITTE FELTENE. Ingenting her er kundens innhold: ingen titler, beløp, filnavn
 * eller meldinger — bare hvor mange, hvem som er superadmin og hvem brukerne er. Plattformens egen
 * organisasjon (`platform`) er aldri med.
 *
 * Aktive brukere og «sist aktiv» per organisasjon måles fra dagen aktivitetsloggingen kom
 * (`measuredFrom`). Lagring er summen av vedleggene; artistbilder har ingen lagret størrelse og
 * telles ikke.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';
import { organizationModuleSchema, type OrganizationModule } from './control-room-modules.js';

/** Kundenes typer. `platform` er aldri en kunde og kommer aldri hit. */
export const CONTROL_ROOM_ORGANIZATION_KINDS = ['festival', 'agency', 'personal'] as const;
export type ControlRoomOrganizationKind = (typeof CONTROL_ROOM_ORGANIZATION_KINDS)[number];

/** `organizations.status`. `suspended` er fryst (sak 238). */
export const CONTROL_ROOM_ORGANIZATION_STATUSES = ['trial', 'active', 'suspended'] as const;
export type ControlRoomOrganizationStatus = (typeof CONTROL_ROOM_ORGANIZATION_STATUSES)[number];

/**
 * Sorteringene lista tilbyr, begge med nøkkelsett: `name` på (lower(navn), id) med indeksen fra
 * 0479, `created` på id (UUIDv7 er opprettelsesrekkefølgen). Ingen sortering på et tall: det ville
 * betydd å telle alle organisasjonene før første rad.
 */
export const CONTROL_ROOM_ORGANIZATION_SORT_KEYS = ['name', 'created'] as const;
export type ControlRoomOrganizationSortKey = (typeof CONTROL_ROOM_ORGANIZATION_SORT_KEYS)[number];

/**
 * HVA ORGANISASJONEN ER, AV MODULENE (QA15-S funn 1, regel 4). Rollene i fast rekkefølge: festival,
 * spillested, konsertarrangør, byrå. Konserter alene er en konsertarrangør; Konserter som Artister tar
 * med (MODULE_REQUIRES) er ikke en rolle til. Spillesteder og Booking er verktøy, ikke roller. Ingen
 * rolle (bare Booking, bare Spillesteder) gir en tom liste, og skjermen faller tilbake på typen.
 */
export const ORGANIZATION_ROLES = ['festival', 'venue', 'promoter', 'agency'] as const;
export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

export function organizationRolesOf(modules: readonly OrganizationModule[]): OrganizationRole[] {
  const on = new Set(modules);
  const roles: OrganizationRole[] = [];
  if (on.has('festival')) roles.push('festival');
  if (on.has('venue')) roles.push('venue');
  if (on.has('concerts') && !on.has('agency')) roles.push('promoter');
  if (on.has('agency')) roles.push('agency');
  return roles;
}

/**
 * SLAGENE I «BRUK» (QA15-S funn 3) og modulene som viser hvert av dem, samme form som
 * `MODULE_DATA_ITEMS`. Tallene kommer fra `app.platform_organization_usage` (1169). Rekkefølgen er
 * den skjermen viser dem i: det modulen handler om først, verktøyene etter.
 */
export const USAGE_ITEMS = {
  festivals: ['festival'],
  editions: ['festival'],
  performances: ['festival'],
  rooms: ['venue'],
  venueNights: ['venue'],
  venueEnquiries: ['venue'],
  venueCrew: ['venue'],
  concerts: ['concerts'],
  dateRequests: ['venues', 'concerts'],
  artists: ['booking', 'agency'],
  bids: ['booking', 'concerts'],
  bookingEnquiries: ['booking', 'agency'],
  soldGigs: ['agency'],
  agreements: ['agency'],
  shifts: ['festival', 'venue', 'concerts'],
  resources: ['festival', 'venue'],
} as const satisfies Record<string, readonly OrganizationModule[]>;
export type UsageItem = keyof typeof USAGE_ITEMS;
export const USAGE_ITEM_KEYS = Object.keys(USAGE_ITEMS) as [UsageItem, ...UsageItem[]];

/** Slagene organisasjonen med disse modulene bruker, i skjermens rekkefølge. */
export function usageItemsFor(modules: readonly OrganizationModule[]): UsageItem[] {
  return USAGE_ITEM_KEYS.filter((item) =>
    (USAGE_ITEMS[item] as readonly OrganizationModule[]).some((module) => modules.includes(module)),
  );
}

export const controlRoomOverviewSchema = z
  .object({
    /** Alle organisasjoner utenom plattformens egen, også personlige. */
    customers: z.number().int().nonnegative(),
    festivals: z.number().int().nonnegative(),
    agencies: z.number().int().nonnegative(),
    personal: z.number().int().nonnegative(),
    frozen: z.number().int().nonnegative(),
    /** Levende brukere med medlemskap i minst én kundeorganisasjon. */
    users: z.number().int().nonnegative(),
    /** Distinkte personer med en levende ekstern deling. */
    externals: z.number().int().nonnegative(),
    /** Distinkte personer med en levende lenkedeling. */
    linkPeople: z.number().int().nonnegative(),
    newOrganizations30: z.number().int().nonnegative(),
    newUsers30: z.number().int().nonnegative(),
    active7: z.number().int().nonnegative(),
    active30: z.number().int().nonnegative(),
    /** Første dag i aktivitetsloggen, eller null før noen har vært aktive. */
    measuredFrom: z.iso.date().nullable(),
  })
  .meta({ id: 'ControlRoomOverview' });

export const controlRoomOrganizationSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    kind: z.enum(CONTROL_ROOM_ORGANIZATION_KINDS),
    /** QA15-S funn 1: modulene som er på. Typen som vises, regnes av dem (`organizationRolesOf`). */
    modules: z.array(organizationModuleSchema).default([]),
    status: z.enum(CONTROL_ROOM_ORGANIZATION_STATUSES),
    createdAt: z.iso.datetime(),
    /** Den første levende superadmin, eller null når organisasjonen ikke har noen. */
    superadmin: z.object({ name: z.string(), email: z.string().nullable() }).nullable(),
    members: z.number().int().nonnegative(),
    externals: z.number().int().nonnegative(),
    linkPeople: z.number().int().nonnegative(),
    /** Årganger, uten konsertbeholderen (`festivals.kind = 'concerts'`). */
    editions: z.number().int().nonnegative(),
    concerts: z.number().int().nonnegative(),
    /** Siste dag noen var aktiv i organisasjonen, fra aktivitetsloggen. */
    lastActiveOn: z.iso.date().nullable(),
    /** Summen av vedleggene i byte. Artistbilder er ikke med. */
    storageBytes: z.number().int().nonnegative(),
  })
  .meta({ id: 'ControlRoomOrganization' });

export const listControlRoomOrganizationsQuerySchema = cursorQuerySchema(
  CONTROL_ROOM_ORGANIZATION_SORT_KEYS,
)
  .extend({
    /** Del av navnet eller sluggen, uten hensyn til store og små bokstaver. */
    search: z.string().trim().min(1).max(120).optional(),
    /** F58: de arkiverte i stedet for de vanlige. Standard: de vanlige. */
    archived: z.stringbool().optional(),
  })
  .meta({ id: 'ListControlRoomOrganizationsQuery' });

export const controlRoomOrganizationListResponseSchema = page(
  controlRoomOrganizationSchema,
  'ControlRoomOrganizationListResponse',
);

/** Siden per organisasjon: tallene fra lista, delene som er tatt i bruk, antall og aktive 7/30. */
export const controlRoomOrganizationDetailSchema = controlRoomOrganizationSchema
  .extend({
    /** Utledet av typen, `festivals.kind`, modulene og om det finnes rader. */
    parts: z.object({
      festival: z.boolean(),
      venue: z.boolean(),
      concerts: z.boolean(),
      booking: z.boolean(),
      agency: z.boolean(),
    }),
    counts: z.object({
      performances: z.number().int().nonnegative(),
      /** Datoforespørsler, forespørsler til spillestedet og forespørsler til byrået. */
      requests: z.number().int().nonnegative(),
      bids: z.number().int().nonnegative(),
      shifts: z.number().int().nonnegative(),
      resources: z.number().int().nonnegative(),
      /** Levende delinger (eksterne og lenker). */
      shares: z.number().int().nonnegative(),
    }),
    active7: z.number().int().nonnegative(),
    active30: z.number().int().nonnegative(),
    /** QA15-S funn 3: antall og siste endring per slag. Skjermen viser slagene modulene gir. */
    usage: z
      .array(
        z.object({
          item: z.enum(USAGE_ITEM_KEYS),
          count: z.number().int().nonnegative(),
          lastAt: z.iso.datetime().nullable(),
        }),
      )
      .default([]),
  })
  .meta({ id: 'ControlRoomOrganizationDetail' });

export const controlRoomOrganizationUserSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string(),
    /** Null for et medlem uten konto. */
    email: z.string().nullable(),
    role: z.string(),
    /** `users.last_login_at`. */
    lastLoginAt: z.iso.datetime().nullable(),
    /** Siste dag brukeren var aktiv i denne organisasjonen, fra aktivitetsloggen. */
    lastActiveOn: z.iso.date().nullable(),
    deactivated: z.boolean(),
  })
  .meta({ id: 'ControlRoomOrganizationUser' });

export const listControlRoomOrganizationUsersQuerySchema = cursorQuerySchema([
  'name',
] as const).meta({ id: 'ListControlRoomOrganizationUsersQuery' });

export const controlRoomOrganizationUserListResponseSchema = page(
  controlRoomOrganizationUserSchema,
  'ControlRoomOrganizationUserListResponse',
);

/**
 * «BRUKERE UTEN ORGANISASJON» (253.41): de personlige kontoene, tatt ut av kundelista. En personlig
 * konto er et sted å stå for en ekstern med en deling eller en artist i artistportalen — en
 * kontotype, ikke en kunde. Raden sier hvem, og hva personen har tilgang til.
 */
export const controlRoomPersonWithoutOrganizationSchema = z
  .object({
    /** Den personlige kontoens organisasjon (lenken til siden for den). */
    organizationId: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    createdAt: z.iso.datetime(),
    lastActiveOn: z.iso.date().nullable(),
    /** Levende delinger (ekstern) personen har, i alle organisasjoner. */
    shares: z.number().int().nonnegative(),
    /** Organisasjonene som har delt noe med personen, høyst fem, i alfabetisk rekkefølge. */
    sharedBy: z.array(z.string()),
  })
  .meta({ id: 'ControlRoomPersonWithoutOrganization' });

export const listControlRoomPeopleWithoutOrganizationQuerySchema = cursorQuerySchema([
  'name',
] as const).meta({ id: 'ListControlRoomPeopleWithoutOrganizationQuery' });

export const controlRoomPeopleWithoutOrganizationResponseSchema = page(
  controlRoomPersonWithoutOrganizationSchema,
  'ControlRoomPeopleWithoutOrganizationResponse',
);

export type ControlRoomPersonWithoutOrganization = z.infer<
  typeof controlRoomPersonWithoutOrganizationSchema
>;
export type ListControlRoomPeopleWithoutOrganizationQuery = z.infer<
  typeof listControlRoomPeopleWithoutOrganizationQuerySchema
>;
export type ControlRoomPeopleWithoutOrganizationResponse = z.infer<
  typeof controlRoomPeopleWithoutOrganizationResponseSchema
>;

export type ControlRoomOverview = z.infer<typeof controlRoomOverviewSchema>;
export type ControlRoomOrganization = z.infer<typeof controlRoomOrganizationSchema>;
export type ListControlRoomOrganizationsQuery = z.infer<
  typeof listControlRoomOrganizationsQuerySchema
>;
export type ControlRoomOrganizationListResponse = z.infer<
  typeof controlRoomOrganizationListResponseSchema
>;
export type ControlRoomOrganizationDetail = z.infer<typeof controlRoomOrganizationDetailSchema>;
export type ControlRoomOrganizationUser = z.infer<typeof controlRoomOrganizationUserSchema>;
export type ListControlRoomOrganizationUsersQuery = z.infer<
  typeof listControlRoomOrganizationUsersQuerySchema
>;
export type ControlRoomOrganizationUserListResponse = z.infer<
  typeof controlRoomOrganizationUserListResponseSchema
>;

/**
 * SMÅ DIAGRAMMER PÅ OVERSIKTEN (QA13 #6, 0940): `GET /v1/platform/overview/series`. Bare tall, som
 * resten av oversikten: aktive brukere per dag (30 dager), nye organisasjoner og brukere per uke
 * (12 uker) og de åtte organisasjonene som bruker mest lagring.
 */
export const controlRoomOverviewSeriesSchema = z
  .object({
    activeByDay: z.array(z.object({ day: z.iso.date(), users: z.number().int().nonnegative() })),
    weekly: z.array(
      z.object({
        weekStart: z.iso.date(),
        organizations: z.number().int().nonnegative(),
        users: z.number().int().nonnegative(),
      }),
    ),
    storageTop: z.array(
      z.object({ organizationId: z.uuid(), name: z.string(), bytes: z.number().nonnegative() }),
    ),
  })
  .meta({ id: 'ControlRoomOverviewSeries' });
export type ControlRoomOverviewSeries = z.infer<typeof controlRoomOverviewSeriesSchema>;
